/**
 * Quick Risk Assessment Component
 * 
 * This component provides a checklist for quickly assessing the risk of third-party applications
 * based on identity, permissions, security, authentication, and visibility factors.
 * It calculates a risk score based on the user's selections.
 */

import React, { useState, useEffect, useRef } from 'react';

const QuickRiskAssessment = () => {
  // Reference for the printable content
  const printableRef = useRef(null);
  
  // State for saved assessments
  const [savedAssessments, setSavedAssessments] = useState(() => {
    const saved = localStorage.getItem('quickRiskAssessments');
    return saved ? JSON.parse(saved) : [];
  });
  
  // State for showing load assessment modal
  const [showLoadModal, setShowLoadModal] = useState(false);
  
  // App information
  const [appName, setAppName] = useState('');
  const [appDescription, setAppDescription] = useState('');
  const [assessorName, setAssessorName] = useState('');
  const [assessmentDate, setAssessmentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });
  
  // State for all checklist items
  const [formData, setFormData] = useState({
    // 1. Identity & Purpose
    verifiedPublisher: 'No',
    clearBusinessNeed: 'No',
    // 2. Permissions & Scope
    leastPrivilege: 'No',
    sensitiveDataAccess: 'No',
    // 3. Security & Compliance
    dataResidencyKnown: 'No',
    complianceCertifications: 'No',
    // 4. Authentication & Authorization
    supportsMFA: 'No',
    conditionalAccessPolicies: 'No',
    // 5. Visibility & Usage
    usageVisibility: 'No',
    regularUsage: 'No'
  });

  // Calculate category totals and overall score
  const [scores, setScores] = useState({
    identityPurpose: 0,
    permissionsScope: 0,
    securityCompliance: 0,
    authenticationAuthorization: 0,
    visibilityUsage: 0,
    total: 0,
    maxPossible: 10,
    percentage: 0
  });

  // Calculate risk level threshold
  const [riskLevel, setRiskLevel] = useState('High');
  
  // Add state for final decision
  const [finalDecision, setFinalDecision] = useState('');

  // Get recommended action based on risk level
  const getRecommendedAction = (level) => {
    switch(level) {
      case 'Low':
        return 'Quick Approval';
      case 'Medium':
        return 'CAB Review Required';
      case 'High':
        return 'Security Review Mandatory';
      default:
        return '';
    }
  };

  // Save as PDF function
  const saveAsPDF = () => {
    // Create a timestamp for the filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `${appName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-risk-assessment-${timestamp}`;
    
    // Store the current page title
    const originalTitle = document.title;
    
    // Set the document title to be used as the PDF filename
    document.title = filename;
    
    // Create a style element for print
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        #printable-content, #printable-content * {
          visibility: visible;
        }
        #printable-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          padding: 20px;
        }
        .no-print {
          display: none !important;
        }
        table {
          page-break-inside: avoid;
          width: 100%;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        .page-break {
          page-break-before: always;
        }
        
        /* PDF Header Styling */
        .pdf-header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e5e7eb;
        }
        
        .pdf-header h1 {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        
        .pdf-app-details {
          margin-bottom: 30px;
          padding: 15px;
          background-color: #f9fafb;
          border-radius: 5px;
          border: 1px solid #e5e7eb;
        }
        
        .pdf-app-details table {
          width: 100%;
        }
        
        .pdf-app-details td {
          padding: 8px;
          vertical-align: top;
        }
        
        .pdf-app-details td:first-child {
          font-weight: bold;
          width: 25%;
        }
        
        /* Better layout for risk score card in PDF */
        .pdf-risk-score {
          margin-bottom: 30px;
          padding: 15px;
          border-radius: 5px;
        }
        
        /* Page footer */
        @page {
          size: A4 portrait;
          margin: 0.5in;
          
          @bottom-center {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 10px;
            color: #6b7280;
          }
        }
      }
    `;
    
    // Add the style to the document head
    document.head.appendChild(style);
    
    // Trigger the print dialog
    window.print();
    
    // Reset the document title
    document.title = originalTitle;
    
    // Remove the style element after printing
    document.head.removeChild(style);
  };

  // Reset form to create a new assessment
  const createNewAssessment = () => {
    if (appName || appDescription || assessorName || finalDecision || Object.values(formData).some(value => value === 'Yes')) {
      if (window.confirm("Creating a new assessment will clear the current one. Are you sure?")) {
        resetForm();
      }
    } else {
      resetForm();
    }
  };
  
  // Reset form helper function
  const resetForm = () => {
    setAppName('');
    setAppDescription('');
    setAssessorName('');
    setAssessmentDate(new Date().toISOString().split('T')[0]);
    setFormData({
      verifiedPublisher: 'No',
      clearBusinessNeed: 'No',
      leastPrivilege: 'No',
      sensitiveDataAccess: 'No',
      dataResidencyKnown: 'No',
      complianceCertifications: 'No',
      supportsMFA: 'No',
      conditionalAccessPolicies: 'No',
      usageVisibility: 'No',
      regularUsage: 'No'
    });
    setFinalDecision('');
  };
  
  // Load a saved assessment
  const loadAssessment = (assessment) => {
    setAppName(assessment.appName || '');
    setAppDescription(assessment.appDescription || '');
    setAssessorName(assessment.assessorName || '');
    setAssessmentDate(assessment.assessmentDate || new Date().toISOString().split('T')[0]);
    setFormData(assessment.formData);
    setFinalDecision(assessment.finalDecision);
    setShowLoadModal(false);
  };
  
  // Delete a saved assessment
  const deleteAssessment = (index) => {
    if (window.confirm("Are you sure you want to delete this assessment?")) {
      const updatedAssessments = [...savedAssessments];
      updatedAssessments.splice(index, 1);
      setSavedAssessments(updatedAssessments);
      localStorage.setItem('quickRiskAssessments', JSON.stringify(updatedAssessments));
    }
  };

  // Save current assessment
  const saveAssessment = () => {
    const assessment = {
      appName,
      appDescription,
      assessorName,
      assessmentDate,
      scores,
      riskLevel,
      finalDecision,
      formData,
      date: new Date().toISOString()
    };
    
    // Check if an assessment with this name already exists
    const existingIndex = savedAssessments.findIndex(a => a.appName === appName);
    
    let updatedAssessments;
    if (existingIndex !== -1) {
      // Update existing assessment
      if (window.confirm(`An assessment for "${appName}" already exists. Do you want to overwrite it?`)) {
        updatedAssessments = [...savedAssessments];
        updatedAssessments[existingIndex] = assessment;
      } else {
        return; // User canceled overwrite
      }
    } else {
      // Add new assessment
      updatedAssessments = [...savedAssessments, assessment];
    }
    
    // Update state and localStorage
    setSavedAssessments(updatedAssessments);
    localStorage.setItem('quickRiskAssessments', JSON.stringify(updatedAssessments));
    
    alert(`Assessment for "${appName}" saved successfully with risk level: ${riskLevel}`);
  };

  // Update scores whenever form data changes
  useEffect(() => {
    const calculateScores = () => {
      // POSITIVE security indicators (Yes = 1 point/safer, No = 0 points/riskier)
      const identityPurpose = 
        (formData.verifiedPublisher === 'Yes' ? 1 : 0) + 
        (formData.clearBusinessNeed === 'Yes' ? 1 : 0);
      
      // MIXED - leastPrivilege is positive (Yes = good)
      // sensitiveDataAccess is negative (No = good)
      const permissionsScope = 
        (formData.leastPrivilege === 'Yes' ? 1 : 0) + 
        (formData.sensitiveDataAccess === 'No' ? 1 : 0);
      
      // POSITIVE security indicators
      const securityCompliance = 
        (formData.dataResidencyKnown === 'Yes' ? 1 : 0) + 
        (formData.complianceCertifications === 'Yes' ? 1 : 0);
      
      // POSITIVE security indicators
      const authenticationAuthorization = 
        (formData.supportsMFA === 'Yes' ? 1 : 0) + 
        (formData.conditionalAccessPolicies === 'Yes' ? 1 : 0);
      
      // POSITIVE security indicators
      const visibilityUsage = 
        (formData.usageVisibility === 'Yes' ? 1 : 0) + 
        (formData.regularUsage === 'Yes' ? 1 : 0);
      
      // Calculate total score - higher score means lower risk
      const total = identityPurpose + permissionsScope + securityCompliance + 
                    authenticationAuthorization + visibilityUsage;
      
      // Calculate percentage
      const percentage = (total / 10) * 100;
      
      // Determine risk level based on total score - higher score = lower risk
      let riskLevel;
      if (total >= 7) {
        riskLevel = 'Low';
      } else if (total >= 4) {
        riskLevel = 'Medium';
      } else {
        riskLevel = 'High';
      }
      
      setScores({
        identityPurpose,
        permissionsScope,
        securityCompliance,
        authenticationAuthorization,
        visibilityUsage,
        total,
        maxPossible: 10,
        percentage
      });
      
      setRiskLevel(riskLevel);
    };
    
    calculateScores();
  }, [formData]);

  // Handle radio button changes
  const handleRadioChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Render a category section with its checklist items
  const renderCategory = (title, maxPoints, fields) => (
    <div className="mb-6 bg-white p-6 rounded-lg shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{title} (Max: {maxPoints} points)</h3>
        <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
          Score: {scores[fields[0].scoreKey]}/{maxPoints}
        </div>
      </div>
      
      <div className="space-y-4">
        {fields.map((field) => {
          // Determine if this is a negative question where "No" is better
          const isNegativeQuestion = field.name === 'sensitiveDataAccess';
          
          return (
            <div key={field.name} className="border-b border-gray-100 pb-4">
              <div className="flex items-start mb-2">
                <div className="flex-1">
                  <p className="font-medium text-gray-700">{field.label}</p>
                  <p className="text-sm text-gray-500">{field.description}</p>
                  {isNegativeQuestion && (
                    <p className="text-xs text-amber-600 mt-1">Note: For this question, "No" is the safer answer</p>
                  )}
                </div>
              </div>
              <div className="flex space-x-4 mt-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio h-4 w-4 text-blue-600"
                    name={field.name}
                    value="Yes"
                    checked={formData[field.name] === 'Yes'}
                    onChange={() => handleRadioChange(title, field.name, 'Yes')}
                  />
                  <span className={`ml-2 text-sm ${isNegativeQuestion ? 'text-red-700' : 'text-green-700'} font-medium`}>
                    Yes {isNegativeQuestion ? '(0 points - Higher Risk)' : '(1 point - Lower Risk)'}
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio h-4 w-4 text-blue-600"
                    name={field.name}
                    value="No"
                    checked={formData[field.name] === 'No'}
                    onChange={() => handleRadioChange(title, field.name, 'No')}
                  />
                  <span className={`ml-2 text-sm ${isNegativeQuestion ? 'text-green-700' : 'text-red-700'} font-medium`}>
                    No {isNegativeQuestion ? '(1 point - Lower Risk)' : '(0 points - Higher Risk)'}
                  </span>
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Main Content */}
      <div id="printable-content" ref={printableRef}>
        {/* PDF Header - Only visible in print */}
        <div className="pdf-header hidden print:block">
          <h1>Quick Risk Assessment</h1>
        </div>
        
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="no-print">
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Quick Risk Assessment</h1>
              <p className="text-gray-600">
                Use this checklist to quickly assess the security risk of third-party applications.
              </p>
            </div>
            
            {/* Assessment Management Buttons - No Print */}
            <div className="flex space-x-2 no-print">
              <button
                onClick={createNewAssessment}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
              >
                New Assessment
              </button>
              <button
                onClick={() => setShowLoadModal(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Load Assessment
              </button>
            </div>
          </div>
          
          {/* Application Information */}
          <div className="bg-white p-4 rounded-lg shadow-sm mb-4 pdf-app-details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="appName" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Application Name*
                  <div className="ml-2 relative group no-print">
                    <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute left-0 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
                      Format: Autotask Ticket number-Application Name
                      <div className="absolute left-0 top-full h-2 w-2 bg-gray-800 transform rotate-45 translate-x-2 -translate-y-1"></div>
                    </div>
                  </div>
                </label>
                <input
                  type="text"
                  id="appName"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  placeholder="e.g., AT12345-Microsoft Teams"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="assessorName" className="block text-sm font-medium text-gray-700 mb-1">
                  Assessor Name*
                </label>
                <input
                  type="text"
                  id="assessorName"
                  value={assessorName}
                  onChange={(e) => setAssessorName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label htmlFor="assessmentDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Assessment Date*
                </label>
                <input
                  type="date"
                  id="assessmentDate"
                  value={assessmentDate}
                  onChange={(e) => setAssessmentDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="appDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Application Description
                </label>
                <textarea
                  id="appDescription"
                  value={appDescription}
                  onChange={(e) => setAppDescription(e.target.value)}
                  placeholder="Briefly describe the application's purpose and functionality"
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
        
        {/* Risk Score Card */}
        <div className={`mb-8 p-6 rounded-lg shadow-md border-l-4 ${
          riskLevel === 'Low' 
            ? 'border-green-500 bg-green-50' 
            : riskLevel === 'Medium'
              ? 'border-yellow-500 bg-yellow-50'
              : 'border-red-500 bg-red-50'
        } pdf-risk-score`}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-800">Risk Assessment Score</h2>
              <p className={`text-lg font-medium ${
                riskLevel === 'Low' 
                  ? 'text-green-600' 
                  : riskLevel === 'Medium'
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }`}>
                {riskLevel} Risk
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Recommended action: <span className="font-medium">{getRecommendedAction(riskLevel)}</span>
              </p>
            </div>
            <div className="text-center">
              <div className={`text-3xl font-bold ${
                riskLevel === 'Low' 
                  ? 'text-green-600' 
                  : riskLevel === 'Medium'
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }`}>
                {scores.total}/{scores.maxPossible}
              </div>
              <div className="text-sm text-gray-500">points</div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 h-4 bg-gray-200 rounded-full overflow-hidden relative">
            <div className="absolute inset-0 flex">
              <div className="h-full bg-red-500" style={{ width: '30%' }}></div>
              <div className="h-full bg-yellow-500" style={{ width: '30%' }}></div>
              <div className="h-full bg-green-500" style={{ width: '40%' }}></div>
            </div>
            {/* Scoring indicator */}
            <div 
              className="absolute h-full w-1 bg-blue-600 transform -translate-x-1/2"
              style={{ 
                left: `${scores.percentage}%`,
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.5)'
              }}
            ></div>
          </div>
          
          <div className="mt-2 text-sm text-gray-600 flex justify-between">
            <span>High Risk (0-3)</span>
            <span>Medium Risk (4-6)</span>
            <span>Low Risk (7-10)</span>
          </div>
        </div>
        
        {/* Risk Level Thresholds Table */}
        <div className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Risk Level Thresholds:</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-4 border text-left text-sm font-medium text-gray-700">Total Score</th>
                  <th className="py-2 px-4 border text-left text-sm font-medium text-gray-700">Risk Level</th>
                  <th className="py-2 px-4 border text-left text-sm font-medium text-gray-700">Recommended Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border text-sm text-gray-800">0-3</td>
                  <td className="py-2 px-4 border">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
                      <span className="text-sm font-medium text-gray-800">HIGH</span>
                    </div>
                  </td>
                  <td className="py-2 px-4 border text-sm text-gray-800">Security Review Mandatory</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border text-sm text-gray-800">4-6</td>
                  <td className="py-2 px-4 border">
                    <div className="flex items-center">
                      <div className="w-4 h-4 text-yellow-500 mr-2">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                          <path d="M12 5.99L19.53 19H4.47L12 5.99M12 2L1 21h22L12 2z"/>
                          <path d="M13 16h-2v2h2v-2zm0-6h-2v4h2v-4z"/>
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-800">MEDIUM</span>
                    </div>
                  </td>
                  <td className="py-2 px-4 border text-sm text-gray-800">CAB Review Required</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border text-sm text-gray-800">7-10</td>
                  <td className="py-2 px-4 border">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
                      <span className="text-sm font-medium text-gray-800">LOW</span>
                    </div>
                  </td>
                  <td className="py-2 px-4 border text-sm text-gray-800">Quick Approval</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Final Decision Section */}
        <div className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <div className="w-5 h-5 text-amber-600 mr-2">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800">Final Decision</h3>
          </div>
          
          <div className="space-y-3 pl-7">
            <div className="flex items-center">
              <input
                type="radio"
                id="approved"
                name="finalDecision"
                value="Approved"
                checked={finalDecision === 'Approved'}
                onChange={(e) => setFinalDecision(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="approved" className="ml-2 text-sm font-medium text-gray-700">
                Approved
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="radio"
                id="furtherReview"
                name="finalDecision"
                value="Further Review Required"
                checked={finalDecision === 'Further Review Required'}
                onChange={(e) => setFinalDecision(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="furtherReview" className="ml-2 text-sm font-medium text-gray-700">
                Further Review Required
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="radio"
                id="rejected"
                name="finalDecision"
                value="Rejected"
                checked={finalDecision === 'Rejected'}
                onChange={(e) => setFinalDecision(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="rejected" className="ml-2 text-sm font-medium text-gray-700">
                Rejected
              </label>
            </div>
          </div>
        </div>
        
        {/* PDF Summary - Only displays clearly in the PDF */}
        <div className="hidden print:block mb-8 p-6 border-t-2 border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Assessment Summary</h3>
          <table className="w-full border-collapse">
            <tbody>
              <tr>
                <td className="py-2 px-4 text-sm font-medium text-gray-700 border-b border-gray-200">Application Name:</td>
                <td className="py-2 px-4 text-sm text-gray-800 border-b border-gray-200">{appName}</td>
              </tr>
              <tr>
                <td className="py-2 px-4 text-sm font-medium text-gray-700 border-b border-gray-200">Description:</td>
                <td className="py-2 px-4 text-sm text-gray-800 border-b border-gray-200">{appDescription || 'N/A'}</td>
              </tr>
              <tr>
                <td className="py-2 px-4 text-sm font-medium text-gray-700 border-b border-gray-200">Assessor:</td>
                <td className="py-2 px-4 text-sm text-gray-800 border-b border-gray-200">{assessorName}</td>
              </tr>
              <tr>
                <td className="py-2 px-4 text-sm font-medium text-gray-700 border-b border-gray-200">Assessment Date:</td>
                <td className="py-2 px-4 text-sm text-gray-800 border-b border-gray-200">
                  {new Date(assessmentDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </td>
              </tr>
              <tr>
                <td className="py-2 px-4 text-sm font-medium text-gray-700 border-b border-gray-200">Risk Score:</td>
                <td className="py-2 px-4 text-sm text-gray-800 border-b border-gray-200">
                  {scores.total}/{scores.maxPossible} ({riskLevel} Risk)
                </td>
              </tr>
              <tr>
                <td className="py-2 px-4 text-sm font-medium text-gray-700 border-b border-gray-200">Final Decision:</td>
                <td className="py-2 px-4 text-sm text-gray-800 border-b border-gray-200">{finalDecision || 'Pending'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Optional page break for better printing */}
        <div className="page-break"></div>
        
        {/* Checklist Categories */}
        {renderCategory("1. Identity & Purpose", 2, [
          { 
            name: 'verifiedPublisher', 
            label: 'Verified Publisher', 
            description: 'Is the app from a trusted, verified publisher? A verified publisher adds accountability and reduces risk.',
            scoreKey: 'identityPurpose'
          },
          { 
            name: 'clearBusinessNeed', 
            label: 'Clear Business Need',
            description: 'Is there a documented business justification for the app? Clear justification helps ensure proper vetting.',
            scoreKey: 'identityPurpose'
          }
        ])}
        
        {renderCategory("2. Permissions & Scope", 2, [
          { 
            name: 'leastPrivilege', 
            label: 'Least Privilege', 
            description: 'Does the app request only the minimum necessary permissions? Excessive permissions increase security risk.',
            scoreKey: 'permissionsScope'
          },
          { 
            name: 'sensitiveDataAccess', 
            label: 'Sensitive Data Access',
            description: 'Does the app request access to sensitive or confidential data? Access to sensitive data increases risk exposure.',
            scoreKey: 'permissionsScope'
          }
        ])}
        
        {renderCategory("3. Security & Compliance", 2, [
          { 
            name: 'dataResidencyKnown', 
            label: 'Data Residency Known', 
            description: 'Do you know in which country or region the app stores data? Known data residency helps with compliance.',
            scoreKey: 'securityCompliance'
          },
          { 
            name: 'complianceCertifications', 
            label: 'Compliance Certifications',
            description: 'Does the app have relevant compliance certifications (ISO, SOC2, GDPR)? Certifications indicate security maturity.',
            scoreKey: 'securityCompliance'
          }
        ])}
        
        {renderCategory("4. Authentication & Authorization", 2, [
          { 
            name: 'supportsMFA', 
            label: 'Supports MFA', 
            description: 'Does the app support multi-factor authentication (MFA)? MFA significantly improves security posture.',
            scoreKey: 'authenticationAuthorization'
          },
          { 
            name: 'conditionalAccessPolicies', 
            label: 'Conditional Access Policies',
            description: 'Can the app be easily blocked or controlled if required? Control capabilities reduce ongoing risk.',
            scoreKey: 'authenticationAuthorization'
          }
        ])}
        
        {renderCategory("5. Visibility & Usage", 2, [
          { 
            name: 'usageVisibility', 
            label: 'Usage Visibility', 
            description: 'Can you easily track and review who is using the app? Visibility helps with security governance.',
            scoreKey: 'visibilityUsage'
          },
          { 
            name: 'regularUsage', 
            label: 'Regular Usage',
            description: 'Is this app expected to be used regularly by staff? Regular usage justifies security investment and attention.',
            scoreKey: 'visibilityUsage'
          }
        ])}
      </div>
      
      {/* Action Buttons - No Print */}
      <div className="mt-8 flex justify-between items-center no-print">
        <button
          onClick={saveAsPDF}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Save as PDF
        </button>
        
        <button
          onClick={saveAssessment}
          disabled={!appName || !assessorName || !finalDecision}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save Assessment
        </button>
      </div>
      
      {/* Load Assessment Modal */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 no-print">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Load Assessment</h3>
              <button
                onClick={() => setShowLoadModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {savedAssessments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No saved assessments found.</p>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Application
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assessor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Risk Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Final Decision
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {savedAssessments.map((assessment, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {assessment.appName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {assessment.assessorName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            assessment.riskLevel === 'Low' 
                              ? 'bg-green-100 text-green-800' 
                              : assessment.riskLevel === 'Medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}>
                            {assessment.riskLevel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(assessment.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {assessment.finalDecision}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => loadAssessment(assessment)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            Load
                          </button>
                          <button
                            onClick={() => deleteAssessment(index)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLoadModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickRiskAssessment; 